import { useEffect, useRef } from "react";

const LERP = 0.11; // ring lag — lower = more lag

const Cursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const mouse = useRef({ x: -200, y: -200 });
  const ring = useRef({ x: -200, y: -200 });
  const hovering = useRef(false);
  const clicking = useRef(false);
  const rafId = useRef(null);

  useEffect(() => {
    // Only run on devices with a fine pointer (mouse, not touch)
    if (!window.matchMedia("(pointer: fine)").matches) return;

    const dot = dotRef.current;
    const ringEl = ringRef.current;
    if (!dot || !ringEl) return;

    const onMove = (e) => {
      mouse.current = { x: e.clientX, y: e.clientY };
      dot.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      dot.classList.add("cursor-visible");
      ringEl.classList.add("cursor-visible");
    };

    const onLeave = () => {
      dot.classList.remove("cursor-visible");
      ringEl.classList.remove("cursor-visible");
    };

    const onEnter = () => {
      dot.classList.add("cursor-visible");
      ringEl.classList.add("cursor-visible");
    };

    const onDown = () => {
      clicking.current = true;
      dot.classList.add("cursor-click");
      ringEl.classList.add("cursor-click");
    };

    const onUp = () => {
      clicking.current = false;
      dot.classList.remove("cursor-click");
      ringEl.classList.remove("cursor-click");
    };

    const onOver = (e) => {
      const target = e.target.closest("a, button, [role='button'], label, .clickable, select");
      if (target && !hovering.current) {
        hovering.current = true;
        dot.classList.add("cursor-hover");
        ringEl.classList.add("cursor-hover");
      } else if (!target && hovering.current) {
        hovering.current = false;
        dot.classList.remove("cursor-hover");
        ringEl.classList.remove("cursor-hover");
      }
    };

    // Animate the lagging ring
    const animate = () => {
      ring.current.x += (mouse.current.x - ring.current.x) * LERP;
      ring.current.y += (mouse.current.y - ring.current.y) * LERP;
      ringEl.style.transform = `translate(${ring.current.x}px, ${ring.current.y}px)`;
      rafId.current = requestAnimationFrame(animate);
    };

    rafId.current = requestAnimationFrame(animate);

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("mouseover", onOver);

    return () => {
      cancelAnimationFrame(rafId.current);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("mouseover", onOver);
    };
  }, []);

  return (
    <>
      <div ref={dotRef} className="cursor-dot" aria-hidden="true" />
      <div ref={ringRef} className="cursor-ring" aria-hidden="true" />
    </>
  );
};

export default Cursor;
